import { login } from '@/api'

export const AUTH_STORAGE_KEYS = {
  menuResources: 'userMenuAuth',
  permissionResources: 'userPermissionResources',
  pageCodes: 'userPageCodes',
  buttonCodes: 'userButtonCodes',
  codes: 'userAuthCodes'
}

const DEFAULT_LANDING_PAGE_CODES = [
  'home',
  'assetsList',
  'assetsLife',
  'nonkeyTool',
  'assetCalibration',
  'assetMaintenance',
  'checkRecord',
  'nonkeyCheckRecord',
  'assetCalibrationRecord',
  'assetMaintenanceRecord',
  'tpmConfirmRecord',
  'sysUser',
  'sysConfig',
  'sysSchedule',
  'notice',
  'integral',
  'employeeFeedback',
  'assetsCheck'
]

// 仅在权限系统中配置的按钮级权限项：其余按钮一律全员开放。
// 点检记录编辑属于数据修正操作，必须严格校验后端下发的按钮权限。
function isControlledButtonCode(buttonCode) {
  if (!buttonCode || typeof buttonCode !== 'string') return false
  const lower = buttonCode.toLowerCase()
  return lower.indexOf('batchdelete') >= 0 ||
    lower.indexOf('batch_delete') >= 0 ||
    buttonCode === 'assetExceptReport.add' ||
    buttonCode === 'checkRecord.detail.edit' ||
    buttonCode === 'nonkeyCheckRecord.detail.edit'
}

function parseStorageJson(key, defaultValue) {
  const rawValue = localStorage.getItem(key)
  if (!rawValue) return defaultValue
  try {
    const parsedValue = JSON.parse(rawValue)
    return parsedValue ?? defaultValue
  } catch (error) {
    return defaultValue
  }
}

function normalizeCodeList(list = []) {
  return Array.from(new Set((list || []).filter(item => typeof item === 'string' && item)))
}

function normalizeResourceList(list = []) {
  return Array.isArray(list) ? list.filter(item => item && item.code) : []
}

export function getPermissionResources() {
  return parseStorageJson(AUTH_STORAGE_KEYS.permissionResources, [])
}

export function getPageCodes() {
  return normalizeCodeList(parseStorageJson(AUTH_STORAGE_KEYS.pageCodes, []))
}

export function getButtonCodes() {
  return normalizeCodeList(parseStorageJson(AUTH_STORAGE_KEYS.buttonCodes, []))
}

export function getAuthCodes() {
  return normalizeCodeList(parseStorageJson(AUTH_STORAGE_KEYS.codes, []))
}

export function hasPageAuth(pageCode) {
  if (!pageCode) return false
  return getPageCodes().includes(pageCode)
}

export function hasBtnAuth(buttonCode) {
  if (!buttonCode) return false
  // 仅受控按钮走权限校验，其余按钮一律放行。
  if (!isControlledButtonCode(buttonCode)) return true
  return getButtonCodes().includes(buttonCode)
}

export function hasAuth(code) {
  if (!code) return false
  // 页面 code 和受控按钮走严格鉴权；其余 code 一律放行。
  if (isControlledButtonCode(code)) return getAuthCodes().includes(code)
  return true
}

export function setPermissionResources(permissionData = {}) {
  const resources = normalizeResourceList(permissionData.resources || [])
  const pageCodes = normalizeCodeList(permissionData.pageCodes || [])
  const buttonCodes = normalizeCodeList(permissionData.buttonCodes || [])
  const codes = normalizeCodeList(permissionData.codes || [...pageCodes, ...buttonCodes])

  localStorage.setItem(AUTH_STORAGE_KEYS.menuResources, JSON.stringify(resources))
  localStorage.setItem(AUTH_STORAGE_KEYS.permissionResources, JSON.stringify(resources))
  localStorage.setItem(AUTH_STORAGE_KEYS.pageCodes, JSON.stringify(pageCodes))
  localStorage.setItem(AUTH_STORAGE_KEYS.buttonCodes, JSON.stringify(buttonCodes))
  localStorage.setItem(AUTH_STORAGE_KEYS.codes, JSON.stringify(codes))

  return {
    resources,
    pageCodes,
    buttonCodes,
    codes
  }
}

export function clearPermissionCache() {
  Object.values(AUTH_STORAGE_KEYS).forEach(key => {
    localStorage.removeItem(key)
  })
}

export function getDefaultAuthorizedRouteName() {
  const pageCodes = getPageCodes()
  const firstPreferredCode = DEFAULT_LANDING_PAGE_CODES.find(code => pageCodes.includes(code))
  return firstPreferredCode || pageCodes[0] || ''
}

export async function fetchAndCachePermissionResources() {
  const response = await login.getPermissionResources()
  const { code, msg, data = {} } = response || {}
  if (code === 0) {
    return {
      code,
      msg,
      data: setPermissionResources(data)
    }
  }
  return {
    code,
    msg,
    data
  }
}

export async function ensurePermissionResourcesLoaded(force = false) {
  if (!force && getAuthCodes().length) {
    return {
      code: 0,
      data: {
        resources: getPermissionResources(),
        pageCodes: getPageCodes(),
        buttonCodes: getButtonCodes(),
        codes: getAuthCodes()
      }
    }
  }
  return fetchAndCachePermissionResources()
}
